import { Button } from 'monday-ui-react-core';
import { Doc, DropdownChevronDown, ExternalPage, NavigationChevronLeft } from 'monday-ui-react-core/icons';
import React from 'react';
import './ViewIntro.css';

/**
 * The Getting started popover's pointer to group templates, shown in an ITEM
 * view only: a board view already lists group templates, and its popover stays
 * plain Getting started (App.renderHelpBubble).
 *
 * Two pieces: <NewViewBanner> sits on the popover's first screen, and its button
 * swaps the popover to <ViewHowTo>, the steps for adding the board view.
 */
const INTRO = {
  title: 'Group Templates',
  blurb: 'Save a whole group, with its items and subitems, as one template.',
  howTitle: 'Use group templates',
  howIntro: 'Group templates live in a board view. Add it once per board:',
  steps: [
    'Open the board this item is on',
    'Click + next to the board’s views',
    'Choose Apps, then Subitem Pro Templates',
    'Create a group template, or apply one',
  ],
};

/**
 * A one-shot burst behind the "New" pill, in monday's logo colours. Each piece
 * flies out along a fixed fan (upwards, so it stays inside the popover's corner
 * of the iframe), then falls and fades; `forwards` leaves it invisible, so
 * nothing has to unmount it. Hidden under prefers-reduced-motion (ViewIntro.css).
 */
const CONFETTI = Array.from({ length: 24 }, (_, i) => {
  const angle = (-170 + (160 * i) / 23) * (Math.PI / 180);
  const dist = 40 + ((i * 37) % 44); // 40–83px, scattered rather than a ring
  return {
    dx: `${Math.round(Math.cos(angle) * dist)}px`,
    dy: `${Math.round(Math.sin(angle) * dist)}px`,
    rot: `${(i % 2 ? 1 : -1) * (180 + ((i * 53) % 360))}deg`,
    delay: `${(i % 4) * 40}ms`,
    color: `var(--spt-confetti-${(i % 5) + 1})`,
    dot: i % 3 === 0,
  };
});

const Confetti = () => (
  <span className="spt-confetti" aria-hidden="true">
    {CONFETTI.map((p, i) => (
      <i
        key={i}
        className={p.dot ? 'spt-confetti-dot' : undefined}
        style={{ '--dx': p.dx, '--dy': p.dy, '--rot': p.rot, '--c': p.color, animationDelay: p.delay }}
      />
    ))}
  </span>
);

// the preview's rows: a status per row, in monday's done / working on it / stuck
const PREVIEW_ROWS = [
  { width: 52, status: 'done' },
  { width: 70, status: 'working' },
  { width: 44, status: 'stuck' },
];

/**
 * A miniature of what a group template holds, drawn in board-table shapes rather
 * than words: a group header over its items. Decorative, so hidden from
 * assistive tech.
 */
const TemplatePreview = () => (
  <div className="spt-preview" aria-hidden="true">
    <div className="spt-preview-group">
      <DropdownChevronDown size={14} />
      Group
    </div>
    <div className="spt-preview-table">
      {PREVIEW_ROWS.map((r, i) => (
        <div key={i} className="spt-preview-row">
          <span className="spt-preview-text" style={{ width: r.width }} />
          <span className={`spt-preview-status spt-preview-status-${r.status}`} />
        </div>
      ))}
    </div>
  </div>
);

/** The "New" announcement on the popover's first screen: a feature card with a preview on top. */
export const NewViewBanner = ({ onShowHow }) => {
  const intro = INTRO;
  return (
    <div className="spt-new-card mb-4">
      <TemplatePreview />
      <div className="px-3 pt-2.5 pb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-primary">{intro.title}</span>
          <span className="relative inline-flex">
            <span className="spt-new-pill">New</span>
            <Confetti />
          </span>
        </div>
        <p className="mt-1 mb-2.5 text-sm leading-relaxed text-secondary">{intro.blurb}</p>
        <Button size={Button.sizes.SMALL} kind={Button.kinds.SECONDARY} onClick={onShowHow}>
          Show me how
        </Button>
      </div>
    </div>
  );
};

/** The popover's second screen: how to add the board view. */
export const ViewHowTo = ({ onBack, guideUrl }) => {
  const intro = INTRO;
  return (
    <div className="px-5 pt-3.5 pb-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 mb-2 -ml-1.5 px-1.5 py-0.5 text-sm text-secondary rounded-md hover:bg-hover hover:text-primary focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <NavigationChevronLeft size={16} />
        Back
      </button>
      <h4 className="mb-1.5 pr-7 font-semibold text-primary">{intro.howTitle}</h4>
      <p className="mb-3 text-sm leading-relaxed text-secondary">{intro.howIntro}</p>
      <ol className="flex flex-col gap-2 mb-4">
        {intro.steps.map((step, i) => (
          <li key={step} className="flex items-start gap-2.5 text-sm text-primary">
            <span className="spt-howto-num">{i + 1}</span>
            <span className="leading-5">{step}</span>
          </li>
        ))}
      </ol>
      <a
        href={guideUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-primary bg-surface border border-strong rounded-md shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors"
      >
        <Doc size={16} className="mr-2" />
        Read more in the user guide
        <ExternalPage size={14} className="ml-2 text-secondary/70" />
      </a>
    </div>
  );
};
